/**
 * Plan 2026-09-28-008 U1 (rulebook R14) — "Reload to see their change", the
 * ONE place a stale Save panel refetches, and only because the rep pressed
 * the button.
 *
 * A stale refusal (a 409 `version_conflict` from any decision, or a
 * `'stale'` usage gate) parks the pending request with `staleRefusal` set
 * and does nothing else — no refetch, no ladder adopt, no draft reseed (see
 * `usage-sidebar-decision-logic.ts#settleUsageSidebarFailure`). The rep's
 * typing stays on screen and on the pending. When they press Reload, this:
 *
 *  1. marks the request `'running'` ({@link beginUsageSidebarReload} — a
 *     no-op, and this returns, unless the request is still current, stale
 *     and idle);
 *  2. refetches the origin's groups — `'play'`: {@link adoptFreshPlayLadder}
 *     (one `GET …/playbooks/:id?playId=`, the moved groups adopted with
 *     `drafts: 'carry'`, so unsaved step text follows the group onto
 *     whatever it now points at); `'playbook'`: the template's detail,
 *     written to the cache ({@link applyLadderSaveToCache}) with this piece's
 *     usage invalidated; `'library'`: the piece's detail and usage
 *     invalidated;
 *  3. a delete pending closes here — there is nothing to re-arm;
 *  4. resolves what the origin group points at NOW for `target.kind`
 *     (a group gone from the fresh ladder → {@link markUsageSidebarOriginMissing},
 *     the existing removal notice);
 *  5. re-arms the SAME request against that piece: its play-scoped library
 *     row (owning play, archived flag, name), a fresh usage read
 *     (`staleTime: 0` — places, `ownVersionPlays`, `templatePlaybooks`), and,
 *     for a steps/conditions draft, the piece's current head (a settings
 *     draft has no head to name). The rep's draft fields are never touched.
 *     {@link retargetUsageSidebarPending} clears `staleRefusal`.
 *
 * Any failure keeps the stale state (sentence, draft, target), puts the
 * request back to `'idle'` so Reload can be pressed again, and toasts one
 * plain sentence. Never throws — the caller is a button's click handler.
 *
 * No React and no `_shared/hooks` import (coding-standards.md §5): the
 * caller (`components/UsageSidebar.tsx`) passes `useApi().apiFetch` and
 * `useQueryClient()` in, and `bun test` targets this module directly.
 */
import { addErrorToast } from '@clay-run/glaze';
import type { QueryClient } from '@tanstack/react-query';

import { getLibrary, getPlaybook, getSegmentation, getSegmentationUsage, getSequence, getSequenceUsage } from './api-client';
import { adoptFreshPlayLadder } from './ensure-play-owned';
import { isArchivedInLibrary, type PlaybooksOwnershipRow } from './play-ownership';
import { applyLadderSaveToCache } from './playbook-query-cache';
import {
  libraryQueryKey,
  segmentationDetailQueryKey,
  segmentationUsageQueryKey,
  sequenceDetailQueryKey,
  sequenceUsageQueryKey,
} from './query-keys';
import type { PlaybookRung, PlaybooksLibraryResponse, PlaybooksUsageResponse } from './schemas';
import {
  beginUsageSidebarReload,
  closeUsageSidebar,
  markUsageSidebarOriginMissing,
  refreshUsageSidebarUsage,
  resetUsageSidebarToIdle,
  retargetUsageSidebarPending,
  useUsageSidebarStore,
  type PendingUsageSidebar,
} from './usage-sidebar-store';

/** The injected throwing `apiFetch` (`_shared/hooks/useApi.ts`), never imported here. */
type ApiFetch = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

/** The one sentence a failed Reload shows — plain, no code, no id (R20). */
export const USAGE_SIDEBAR_RELOAD_FAILED_MESSAGE = 'Could not reload this play — try again.';

type TargetKind = PendingUsageSidebar['target']['kind'];

/** What `rung` points at for `kind` — its segment, or its sequence (`undefined` for an unmapped group). */
function slotOf(rung: PlaybookRung, kind: TargetKind): string | undefined {
  return kind === 'segmentation' ? rung.segmentationId : (rung.mapping?.sequenceId ?? undefined);
}

/**
 * Step 2 of the module doc — refetches the origin's groups and returns the
 * fresh ladder's rungs, or `null` when there is no group ladder to consult
 * (a Library-page origin, or a playbook with no head yet).
 */
async function refetchOriginGroups(
  apiFetch: ApiFetch,
  queryClient: QueryClient,
  pending: PendingUsageSidebar,
): Promise<PlaybookRung[] | null> {
  const { origin, target } = pending;
  if (origin.kind === 'play') {
    const head = await adoptFreshPlayLadder(apiFetch, queryClient, {
      playbookId: origin.playbookId,
      playId: origin.playId,
      kind: target.kind,
      identityIds: [target.id],
    });
    return head?.rungs ?? null;
  }
  const detailKey = target.kind === 'segmentation' ? segmentationDetailQueryKey : sequenceDetailQueryKey;
  const usageKey = target.kind === 'segmentation' ? segmentationUsageQueryKey : sequenceUsageQueryKey;
  if (origin.kind === 'playbook') {
    const fresh = await getPlaybook(apiFetch, origin.playbookId);
    if (fresh.head) {
      await applyLadderSaveToCache(queryClient, origin.playbookId, {
        versionId: fresh.head.versionId,
        versionNumber: fresh.head.versionNumber,
        rungs: fresh.head.rungs,
      });
    }
    await queryClient.invalidateQueries({ queryKey: usageKey(target.id) });
    return fresh.head?.rungs ?? null;
  }
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: detailKey(target.id) }),
    queryClient.invalidateQueries({ queryKey: usageKey(target.id) }),
  ]);
  return null;
}

/**
 * Step 4 — the id the origin group points at now: `undefined` when the
 * group is gone (or no longer points at anything of `target.kind`). An
 * origin with no group ladder, or a group with no server id yet, keeps the
 * target it was opened for.
 */
function currentTargetId(pending: PendingUsageSidebar, freshRungs: PlaybookRung[] | null): string | undefined {
  const { origin, target } = pending;
  if (origin.kind === 'library' || origin.rungId === null || freshRungs === null) return target.id;
  const group = freshRungs.find((rung) => rung.id === origin.rungId);
  return group === undefined ? undefined : slotOf(group, target.kind);
}

export async function reloadUsageSidebarAfterStale(
  apiFetch: ApiFetch,
  queryClient: QueryClient,
  requestId: string,
): Promise<void> {
  const pending = useUsageSidebarStore.getState().pending;
  if (pending === null || pending.requestId !== requestId) return;
  if (!beginUsageSidebarReload(requestId)) return;
  const isStillCurrent = () => useUsageSidebarStore.getState().pending?.requestId === requestId;
  const { origin, target } = pending;

  try {
    const freshRungs = await refetchOriginGroups(apiFetch, queryClient, pending);
    if (!isStillCurrent()) return;
    if (pending.mode === 'delete') {
      closeUsageSidebar();
      return;
    }

    const id = currentTargetId(pending, freshRungs);
    if (id === undefined) {
      markUsageSidebarOriginMissing(requestId);
      return;
    }

    const playId = origin.kind === 'play' ? origin.playId : undefined;
    const [library, usage, head] = await Promise.all([
      queryClient.fetchQuery<PlaybooksLibraryResponse>({
        queryKey: libraryQueryKey(playId),
        queryFn: () => getLibrary(apiFetch, playId === undefined ? {} : { playId }),
        staleTime: 0,
      }),
      queryClient.fetchQuery<PlaybooksUsageResponse>({
        queryKey: target.kind === 'segmentation' ? segmentationUsageQueryKey(id) : sequenceUsageQueryKey(id),
        queryFn: () =>
          target.kind === 'segmentation' ? getSegmentationUsage(apiFetch, id) : getSequenceUsage(apiFetch, id),
        staleTime: 0,
      }),
      // A settings draft names no head (its pending's `headVersionId` stays as opened).
      pending.draftSettings !== undefined
        ? Promise.resolve(undefined)
        : target.kind === 'segmentation'
          ? queryClient
              .fetchQuery({
                queryKey: segmentationDetailQueryKey(id),
                queryFn: () => getSegmentation(apiFetch, id),
                staleTime: 0,
              })
              .then((detail) => detail.head?.versionId ?? null)
          : queryClient
              .fetchQuery({
                queryKey: sequenceDetailQueryKey(id),
                queryFn: () => getSequence(apiFetch, id),
                staleTime: 0,
              })
              .then((detail) => detail.head?.versionId ?? null),
    ]);
    if (!isStillCurrent()) return;

    const rows: readonly (PlaybooksOwnershipRow & { name: string })[] =
      target.kind === 'segmentation' ? library.segmentations : library.sequences;
    const row = rows.find((candidate) => candidate.id === id);
    retargetUsageSidebarPending(requestId, {
      id,
      owningPlayId: row === undefined ? (id === target.id ? target.owningPlayId : null) : row.owningPlayId,
      places: usage.places,
      ...(head === undefined ? {} : { headVersionId: head }),
      ...(row === undefined ? {} : { name: row.name, archivedInLibrary: isArchivedInLibrary(row) }),
    });
    refreshUsageSidebarUsage(requestId, usage);
    resetUsageSidebarToIdle(requestId);
  } catch {
    if (!isStillCurrent()) return;
    resetUsageSidebarToIdle(requestId);
    addErrorToast({ content: USAGE_SIDEBAR_RELOAD_FAILED_MESSAGE });
  }
}
